import { cn } from '@/lib/utils';
import type { SabotageBanner } from '@/lib/nflSabotageBanners';

/**
 * SAB-931: the sabotage banners, restored to the V2 room.
 *
 * Five of seven draft sabotage effects had no UI here at all. Three are enforced
 * server-side, so they still HAPPENED — the target's clock really was halved, their pick
 * really was auto-drafted — they just had no idea why. This is the explanation.
 *
 * ── PRESENTATION ONLY ───────────────────────────────────────────────────────
 *
 * Which banners exist, and for whom, is decided by `deriveSabotageBanners` — a pure,
 * mutation-checked function. That split is deliberate: "is this banner shown to exactly the
 * right viewer" is a claim about a PAID card working, and it belongs somewhere testable
 * rather than inside JSX.
 *
 * ── NOT A FULL-SCREEN OVERLAY ───────────────────────────────────────────────
 *
 * The legacy room renders Blocked Punt as a fixed inset-0 overlay over the whole screen.
 * Deliberately not ported that way: this room serves a live draft with a running clock, and
 * covering the board while someone is on the clock is a worse outcome than the sabotage
 * itself. `urgent` gets a pulse and a stronger border instead of taking the screen.
 */

export interface SabotageBannersProps {
  banners: SabotageBanner[];
  className?: string;
}

export default function SabotageBanners({ banners, className }: SabotageBannersProps) {
  if (banners.length === 0) return null;

  return (
    <div data-testid="sabotage-banners" className={cn('flex flex-col gap-1.5', className)}>
      {banners.map((b) => (
        <div
          key={b.kind}
          data-testid={`sabotage-banner-${b.kind}`}
          data-tone={b.tone}
          // role="status" rather than "alert": these are ambient conditions the manager
          // needs to know, not interruptions demanding they stop what they are doing --
          // and an assertive live region firing mid-pick would fight the clock.
          role="status"
          className={cn(
            'flex items-start gap-2.5 rounded-[10px] border px-3 py-2',
            b.tone === 'league'
              ? 'border-gold/50 bg-gold/[0.10]'
              : 'border-damage/55 bg-damage/[0.12]',
            b.urgent && 'animate-pulse',
          )}
        >
          <span className="text-base leading-none flex-none" aria-hidden="true">{b.emoji}</span>
          <div className="min-w-0">
            <p className={cn(
              'font-display text-[11.5px] font-bold uppercase tracking-[0.04em] leading-tight',
              b.tone === 'league' ? 'text-gold' : 'text-damage',
            )}>
              {b.title}
            </p>
            {b.detail && (
              <p
                data-testid={`sabotage-detail-${b.kind}`}
                className="mt-0.5 text-[11px] leading-tight text-muted-foreground tabular-nums"
              >
                {b.detail}
              </p>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
